import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useDebounceValue } from 'usehooks-ts';
import axios from 'axios';
import { plato_api_model_correlation_candidates_tags_path } from '@/modules/routes';
import QueryKeys from '@/utils/QueryKeys';
import { CandidateStandard, CandidateStandardsResponse, Filters } from '../types';

export const PAGE_SIZE = 25;

const SEARCH_DEBOUNCE = 300;

// Each page remembers the scope it was fetched for, so "these rows are the wrong framework's" is a
// fact about the data rather than a guess about render order.
const fetchCandidates = (scope: string, params: Record<string, unknown>) => (
  axios
    .get<CandidateStandardsResponse>(plato_api_model_correlation_candidates_tags_path(), { params })
    .then(response => ({ ...response.data, scope }))
);

type CandidatePage = CandidateStandardsResponse & { scope: string };

const nextPage = (lastPage: CandidatePage, allPages: CandidatePage[]) => {
  const fetched = allPages.reduce((sum, page) => sum + page.data.length, 0);

  return fetched < lastPage.meta.count ? allPages.length + 1 : undefined;
};

interface UseCorrelationCandidatesParams {
  enabled: boolean;
  filters: Filters;
  modelId: number;
  modelType: string;
}

// Server-filtered rather than client-filtered like the table: EdGate is ~79.5k leaf standards
// before a state or grade narrows it, so the pool can never ship whole.
export default function useCorrelationCandidates({
  enabled, filters, modelId, modelType,
}: UseCorrelationCandidatesParams) {
  const [search] = useDebounceValue(filters.search.trim(), SEARCH_DEBOUNCE);

  const scope = [filters.category, filters.state, filters.grade].join('|');

  const params = {
    category: filters.category || undefined,
    grade: filters.grade || undefined,
    model_id: modelId,
    model_type: modelType,
    search: search || undefined,
    state_id: filters.state || undefined,
  };

  const query = useInfiniteQuery({
    enabled,
    getNextPageParam: nextPage,
    // Typing narrows the list you are already looking at, so the old rows stay put while the next
    // page loads.
    keepPreviousData: true,
    queryFn: ({ pageParam = 1 }) => fetchCandidates(scope, { ...params, page: pageParam, per_page: PAGE_SIZE }),
    queryKey: [
      QueryKeys.CorrelationCandidates, modelType, modelId,
      filters.category, filters.state, filters.grade, search,
    ],
  });

  // Switching framework, state or grade doesn't narrow the list, it replaces it — so the rows held
  // over from the previous scope are simply the wrong ones, and the panel shows its loader instead.
  const stale = query.data !== undefined && query.data.pages.at(-1)?.scope !== scope;

  const candidates = useMemo<CandidateStandard[]>(
    () => (stale ? [] : query.data?.pages.flatMap(page => page.data) ?? []),
    [query.data, stale],
  );

  return {
    candidates,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: !!query.hasNextPage,
    isError: query.isError,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isInitialLoading || stale,
    // Lags the input by the debounce, so it describes the list actually on screen.
    searchTerm: search,
    // The last page, not the first: every page carries the same unpaginated total, and the last one
    // is the most recently fetched, so it can't disagree with the candidates built from those pages.
    totalCount: query.data?.pages.at(-1)?.meta.count ?? 0,
  };
}
